import { ApiError } from "@simpro/api-client";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { useLocation } from "react-router";
import { salonProfileQuery } from "@/features/salon";
import { branchSlug, CHOOSE_BRANCH_QUERY, openBranch } from "@/lib/branch";
import { BranchPicker } from "./BranchPicker";

/** Pages that are the customer's or the salon's, not a branch's: they need no branch chosen. */
const BUSINESS_WIDE = ["/appointments", "/account", "/articles", "/gallery"];

/**
 * What a page shows before it is a branch's (design note salon-multi-branch.md 9.4; FR-SA-245,
 * FR-SA-248). A branch link that is an old one moves to the branch's current link; one to a closed or
 * unknown branch shows the picker saying so. A bare link at a salon with several branches shows the
 * picker, except on the customer's own pages and the salon's articles and gallery. A salon with one
 * branch shows its page as it always has.
 */
export function BranchFrame({ children }: { children: ReactNode }) {
  const { pathname, search } = useLocation();
  const profile = useQuery(salonProfileQuery());
  const canonical = profile.data?.branch?.slug;

  // FR-SA-232: a printed QR code with an old link opens the branch, under its new link.
  useEffect(() => {
    if (branchSlug && canonical && canonical !== branchSlug) {
      openBranch(canonical, `${pathname}${search}`);
    }
  }, [canonical, pathname, search]);

  const code = profile.error instanceof ApiError ? profile.error.code : undefined;
  if (branchSlug && (code === "salon.branch_closed" || code === "salon.branch_not_found")) {
    return <BranchPicker salonName="" reason={code === "salon.branch_closed" ? "closed" : "unknown"} />;
  }

  const salon = profile.data;
  const choosing = new URLSearchParams(search).has(CHOOSE_BRANCH_QUERY);
  const needsBranch = !BUSINESS_WIDE.some((page) => pathname.startsWith(page));
  if (salon && !salon.branch && (salon.branchCount > 1 || choosing) && needsBranch) {
    return <BranchPicker salonName={salon.displayName} />;
  }

  return <>{children}</>;
}
